// Modules
import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Breadcrumb, Breadcrumbs, Button, Icon, Intent, ProgressBar, Spinner, Tag } from '@blueprintjs/core'
import { nanoid } from 'nanoid'
import PropTypes from 'prop-types'

// Components
import Fallback from 'components/presentation/fallback/Fallback'

// Hooks
import useLanguage from 'hooks/system/useLanguage'

// Helpers
import { convertFontSizeToPixelSize } from 'helpers/size'

// Styles
import './Header.scss'

// Component: Layout > Page > Header
function Header ({ breadcrumbs, canGoBack, canGoUp, children, controls, defaultSelectedTab, hasData, isFixed, isLoading, link, name, onClickBack, onClickTab, onClickUp, progress, style, subtitle, tabs, title }) {
	// Data
	const navigate = useNavigate()
	const { direction, language } = useLanguage('components.layout.page.components.header')

	// State
	const [backtrack, setBacktrack] = useState(null)
	const [selectedTab, setSelectedTab] = useState(defaultSelectedTab || tabs?.[0]?.label)

	// Functions
	function renderBreadcrumb ({ text, isActive, isDisabled, ...rest }) {
		return (
			<Breadcrumb
				{...rest}
				disabled={isDisabled}
				current={isActive}
				className={
					'aix-breadcrumb' +
					(isActive ? ' aix-breadcrumb-current' : '') +
					(isDisabled ? ' aix-breadcrumb-disabled' : '')
				}>
				{text}
			</Breadcrumb>
		)
	}
	function renderTabbing (tabs) {
		return (
			<ul className="aix-header-tabs">
				{tabs.map(tab =>
					<li
						key={tab.id}
						className={tab.id === selectedTab ? 'aix-active' : ''}
						onClick={() => {
							setSelectedTab(tab.id)
							if (!!onClickTab) onClickTab(tab.id)
						}}>
						{tab.label}
					</li>
				)}
			</ul>
		)
	}

	// Functions (Memoized)
	const goBack = useCallback(() => {
		navigate(-1)
		setBacktrack({ id: nanoid(), target: window.location.href })
	}, [navigate])

	// Effects
	useEffect(() => {
		let timer
		if (!!backtrack) {
			timer = setTimeout(() => {
				if (backtrack.target === window.location.href) goBack()
				else setBacktrack(null)
			}, 10)
		}
		return () => clearTimeout(timer)
	}, [backtrack, goBack])

	// Render
	return (
		<header
			data-header={name}
			data-component="header"
			className={
				'aix-header' +
				(isFixed ? ' aix-sticky' : '') +
				(tabs ? ' aix-tabbed' : '')
			}
			style={style}>
			{canGoBack &&
				<Button
					large
					minimal
					className="aix-header-back"
					icon={`arrow-${direction === 'ltr' ? 'left' : 'right'}`}
					onClick={() => onClickBack ? onClickBack() : goBack()} />
			}
			{canGoUp &&
				<Button
					large
					minimal
					className="aix-header-up"
					icon={`arrow-top-${direction === 'ltr' ? 'left' : 'right'}`}
					onClick={() => onClickUp ? onClickUp() : navigate('../')} />
			}
			{tabs
				? renderTabbing(tabs)
				: title && (
					<div className="aix-header-information">
						<h2
							className={
								'aix-title' +
								(link ? ' aix-link' : '')
							}
							onClick={link ? () => navigate(link) : null}>
							{isLoading &&
								<Spinner
								className="aix-title-loader"
								size={convertFontSizeToPixelSize('.75rem')} />
							}
							{typeof title === 'object'
								? <>
									<span
										data-label={title.label}
										data-prefix={title.prefix}
										data-suffix={title.suffix}
										className="aix-title-text">
										{title.main}
									</span>
									{title.badge && (
										typeof title.badge === 'string'
											? <span className="aix-title-badge">{title.badge.toLocaleUpperCase(language)}</span>
											: title.badge
									)}
									{title.action &&
										<Button
											small
											minimal
											className="aix-title-action"
											icon={title.action.icon || null}
											intent={title.action.intent || Intent.NONE}
											onClick={title.action.onClick}>
											{title.action.text && <span>{title.action.text}</span>}
										</Button>
									}
								</>
								: <span className="aix-title-text">{title}</span>
							}
							{link &&
								<Icon
									className="aix-title-icon"
									icon={`arrow-top-${direction === 'rtl' ? 'left' : 'right'}`}
									size={convertFontSizeToPixelSize('.625rem')} />
							}
						</h2>
						{progress?.message
							? <Tag
								minimal
								className="aix-message"
								intent={progress?.intent}>
								{progress?.message}
							</Tag>
							: <>
								{subtitle &&
									<div className="aix-subtitle">
										<Fallback
											hasData={typeof hasData === 'boolean' ? hasData : true}
											isUpdating={isLoading}>
											{(typeof subtitle === 'object' && subtitle.main)
												? <>
													<span
														data-label={subtitle.label}
														data-prefix={subtitle.prefix}
														data-suffix={subtitle.suffix}
														className={
															'aix-subtitle-text' +
															(subtitle.isInactive ? ' aix-inactive' : '')
														}>
														{subtitle.main}
													</span>
													{subtitle.badge && <span className="aix-subtitle-badge">{subtitle.badge.toLocaleUpperCase(language)}</span>}
												</>
												: <span className="aix-subtitle-text">{subtitle}</span>
											}
										</Fallback>
									</div>
								}
								{breadcrumbs &&
									<Breadcrumbs
										items={breadcrumbs}
										className="aix-breadcrumbs"
										breadcrumbRenderer={renderBreadcrumb} />
								}
							</>
						}
					</div>
				)
			}
			{children && <div className="aix-header-content">{children}</div>}
			{controls && <div className="aix-controls aix-header-controls">{controls}</div>}
			{typeof(progress?.percentage) === 'number' &&
				<div className="aix-header-progress">
					<ProgressBar
						value={progress.percentage / 100}
						intent={progress?.intent}
						className="aix-progress" />
				</div>
			}
		</header>
	)
}

// Properties
Header.propTypes = {
	breadcrumbs: PropTypes.array,
	canGoBack: PropTypes.bool,
	canGoUp: PropTypes.bool,
	children: PropTypes.node,
	controls: PropTypes.node,
	defaultSelectedTab: PropTypes.string,
	hasData: PropTypes.bool,
	isFixed: PropTypes.bool,
	isLoading: PropTypes.bool,
	link: PropTypes.string,
	name: PropTypes.string.isRequired,
	onClickBack: PropTypes.func,
	onClickTab: PropTypes.func,
	onClickUp: PropTypes.func,
	progress: PropTypes.shape({
		intent: PropTypes.string,
		isDone: PropTypes.bool,
		message: PropTypes.string,
		percentage: PropTypes.number
	}),
	style: PropTypes.object,
	subtitle: PropTypes.oneOfType([
		PropTypes.node,
		PropTypes.shape({
			badge: PropTypes.oneOfType([
				PropTypes.number,
				PropTypes.string
			]),
			main: PropTypes.string.isRequired,
			prefix: PropTypes.oneOfType([
				PropTypes.node,
				PropTypes.number,
				PropTypes.string
			]),
			suffix: PropTypes.oneOfType([
				PropTypes.node,
				PropTypes.number,
				PropTypes.string
			])
		}),
		PropTypes.string
	]),
	tabs: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string.isRequired,
			label: PropTypes.string.isRequired
		}).isRequired
	),
	title: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.shape({
			action: PropTypes.shape({
				icon: PropTypes.string,
				intent: PropTypes.string,
				onClick: PropTypes.func.isRequired,
				text: PropTypes.string
			}),
			badge: PropTypes.oneOfType([
				PropTypes.number,
				PropTypes.string
			]),
			main: PropTypes.string.isRequired,
			prefix: PropTypes.oneOfType([
				PropTypes.node,
				PropTypes.number,
				PropTypes.string
			]),
			suffix: PropTypes.oneOfType([
				PropTypes.node,
				PropTypes.number,
				PropTypes.string
			])
		})
	])
}
Header.defaultProps = {
	canGoBack: false,
	canGoUp: false,
	isFixed: false,
	isLoading: false
}

// Export
export default Header
